import { useTranslation } from 'react-i18next'

import { brand, fonts } from '../brand'
import { anim } from '../theme'

/**
 * Demo do hero: o widget de atendimento do produto montado sobre um site de cliente (faux),
 * vestido na identidade Codout — janela tinta com chrome de terminal (traffic-lights), respostas
 * com DADO AO VIVO citando a fonte (chip ciano), respostas rápidas e um cartão de handoff. É o
 * análogo, para este SaaS de suporte, do "terminal" que o codout.com usa como visual do hero.
 * Animação em CSS puro (ciclo de 9s); as mensagens seguem animando sob reduced-motion (só o
 * movimento decorativo — `data-decor` — é desligado).
 */
export function WebChatWidgetDemo() {
  const { t } = useTranslation()

  const dot = (animation: string) => (
    <span
      aria-hidden="true"
      style={{ width: 7, height: 7, borderRadius: '50%', background: brand.slate, animation }}
    />
  )

  const macDot = (color: string) => (
    <span
      aria-hidden="true"
      style={{ width: 10, height: 10, borderRadius: '50%', background: color, display: 'block' }}
    />
  )

  return (
    <div style={{ position: 'relative', animation: anim.fade }}>
      <div
        style={{
          position: 'relative',
          minHeight: 588,
          borderRadius: 18,
          border: `1px solid ${brand.line}`,
          background: 'linear-gradient(160deg, #eef2f6, #e2e8ee)',
          boxShadow: brand.shadowSm,
          overflow: 'hidden',
        }}
      >
        {/* Faux client site atrás do widget, com chrome de terminal (motivo Codout) */}
        <div
          aria-hidden="true"
          style={{ position: 'absolute', inset: 0, padding: 20, opacity: 0.6, userSelect: 'none' }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 22 }}>
            {macDot(brand.termRed)}
            {macDot(brand.termAmber)}
            {macDot(brand.termGreen)}
            <div style={{ display: 'flex', gap: 14, marginLeft: 12 }}>
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  style={{
                    display: 'block',
                    width: 46,
                    height: 9,
                    borderRadius: 5,
                    background: brand.slate,
                    opacity: 0.32,
                  }}
                />
              ))}
            </div>
          </div>
          <div
            style={{
              height: 15,
              width: '62%',
              borderRadius: 7,
              background: brand.slate,
              opacity: 0.34,
              margin: '8px 0',
            }}
          />
          <div
            style={{
              height: 15,
              width: '42%',
              borderRadius: 7,
              background: brand.slate,
              opacity: 0.22,
              margin: '8px 0',
            }}
          />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 12,
              marginTop: 26,
            }}
          >
            {[0, 1, 2].map((i) => (
              <div
                key={i}
                style={{
                  height: 96,
                  borderRadius: 12,
                  background: brand.paper,
                  opacity: 0.55,
                  border: `1px solid ${brand.line}`,
                }}
              />
            ))}
          </div>
        </div>
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 16,
            right: 18,
            fontFamily: fonts.mono,
            fontSize: 10.5,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: brand.slate,
            fontWeight: 500,
          }}
        >
          {t('landing.hero.webchat.siteTag')}
        </span>

        {/* Painel do widget */}
        <div
          data-decor=""
          style={{
            position: 'absolute',
            right: 22,
            bottom: 22,
            width: 'min(360px, calc(100% - 40px))',
            display: 'flex',
            flexDirection: 'column',
            height: 512,
            maxHeight: 'calc(100% - 44px)',
            background: brand.chip,
            border: `1px solid ${brand.line}`,
            borderRadius: brand.radius,
            boxShadow: brand.shadowLg,
            overflow: 'hidden',
            animation: anim.float,
          }}
        >
          {/* Cabeçalho tinta */}
          <div
            style={{
              background: brand.ink,
              color: '#fff',
              padding: '15px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span
              style={{
                position: 'relative',
                width: 40,
                height: 40,
                borderRadius: 11,
                background: 'rgba(255,255,255,0.12)',
                display: 'grid',
                placeItems: 'center',
                fontFamily: fonts.display,
                fontWeight: 700,
                fontSize: 15,
              }}
              aria-hidden="true"
            >
              {t('landing.hero.webchat.agentInitial')}
              <span
                data-decor=""
                style={{
                  position: 'absolute',
                  right: -2,
                  bottom: -2,
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  background: brand.termGreen,
                  border: `2px solid ${brand.ink}`,
                  animation: anim.pulseSlow,
                }}
              />
            </span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <b
                style={{
                  fontFamily: fonts.display,
                  fontWeight: 600,
                  fontSize: 15,
                  display: 'block',
                }}
              >
                {t('landing.hero.webchat.agentName')}
              </b>
              <span
                style={{
                  fontFamily: fonts.mono,
                  fontSize: 11,
                  color: 'rgba(255,255,255,0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span
                  aria-hidden="true"
                  style={{ width: 7, height: 7, borderRadius: '50%', background: brand.termGreen }}
                />
                {t('landing.hero.webchat.agentStatus')}
              </span>
            </div>
            <span
              aria-hidden="true"
              style={{
                background: 'rgba(255,255,255,0.1)',
                width: 30,
                height: 30,
                borderRadius: 9,
                display: 'grid',
                placeItems: 'center',
              }}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.4}
                width={16}
                height={16}
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </span>
          </div>

          {/* Corpo */}
          <div
            style={{
              flex: 1,
              overflow: 'hidden',
              padding: '18px 16px 8px',
              display: 'flex',
              flexDirection: 'column',
              gap: 12,
            }}
          >
            {/* Saudação — já sabe quem é */}
            <div style={{ alignSelf: 'flex-start', maxWidth: '86%', fontSize: 14 }}>
              <div
                style={{
                  padding: '10px 13px',
                  borderRadius: '14px 14px 14px 4px',
                  background: brand.agentBubble,
                  color: brand.ink,
                  border: `1px solid ${brand.line}`,
                  boxShadow: brand.shadowSm,
                  lineHeight: 1.5,
                }}
              >
                {t('landing.hero.webchat.greetingHello')}{' '}
                <b style={{ fontWeight: 700 }}>{t('landing.hero.webchat.customerName')}</b>
                {t('landing.hero.webchat.greetingSeen')}{' '}
                <b style={{ fontWeight: 700 }}>{t('landing.hero.webchat.planName')}</b>
                {t('landing.hero.webchat.greetingHelp')}
              </div>
            </div>

            {/* Pergunta do cliente */}
            <div
              style={{ alignSelf: 'flex-end', maxWidth: '84%', fontSize: 14, animation: anim.ask }}
            >
              <div
                style={{
                  padding: '10px 13px',
                  borderRadius: '14px 14px 4px 14px',
                  background: brand.userBubble,
                  color: brand.userText,
                }}
              >
                {t('landing.hero.webchat.customerAsk')}
              </div>
            </div>

            {/* Digitando */}
            <div
              style={{
                alignSelf: 'flex-start',
                display: 'inline-flex',
                gap: 4,
                padding: '13px 14px',
                borderRadius: '14px 14px 14px 4px',
                background: brand.agentBubble,
                border: `1px solid ${brand.line}`,
                boxShadow: brand.shadowSm,
                animation: anim.typing,
              }}
            >
              {dot(anim.blink1)}
              {dot(anim.blink2)}
              {dot(anim.blink3)}
            </div>

            {/* Resposta com dado ao vivo + fonte */}
            <div
              style={{
                alignSelf: 'flex-start',
                maxWidth: '90%',
                fontSize: 14,
                animation: anim.suggestion,
              }}
            >
              <div
                style={{
                  padding: '10px 13px',
                  borderRadius: '14px 14px 14px 4px',
                  background: brand.agentBubble,
                  color: brand.ink,
                  border: `1px solid ${brand.line}`,
                  boxShadow: brand.shadowSm,
                  lineHeight: 1.5,
                }}
              >
                {t('landing.hero.webchat.answerPrefix')}{' '}
                <b style={{ fontWeight: 700 }}>{t('landing.hero.webchat.orderNumber')}</b>{' '}
                {t('landing.hero.webchat.answerSuffix')}
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    marginTop: 8,
                    fontFamily: fonts.mono,
                    fontSize: 10.5,
                    color: brand.signalDeep,
                    background: 'rgba(18, 106, 135, 0.1)',
                    padding: '4px 9px',
                    borderRadius: 6,
                    fontWeight: 500,
                    letterSpacing: '0.02em',
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    width={12}
                    height={12}
                  >
                    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
                  </svg>
                  {t('landing.hero.webchat.sourceLabel')} {t('landing.hero.webchat.sourceTool')}
                </span>
              </div>
            </div>

            {/* Respostas rápidas */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 7,
                alignSelf: 'flex-start',
                maxWidth: '94%',
                animation: anim.replies,
              }}
            >
              {[
                t('landing.hero.webchat.quickReply1'),
                t('landing.hero.webchat.quickReply2'),
                t('landing.hero.webchat.quickReply3'),
              ].map((label) => (
                <span
                  key={label}
                  style={{
                    fontSize: 13,
                    fontWeight: 500,
                    color: brand.ink,
                    background: brand.paper,
                    border: `1px solid ${brand.lineStrong}`,
                    padding: '7px 12px',
                    borderRadius: 100,
                  }}
                >
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* Rodapé */}
          <div
            style={{
              borderTop: `1px solid ${brand.line}`,
              background: brand.paper,
              padding: '10px 12px 8px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: brand.chip,
                border: `1px solid ${brand.line}`,
                borderRadius: 12,
                padding: '6px 6px 6px 13px',
              }}
            >
              <span style={{ flex: 1, fontSize: 14, color: brand.slate }}>
                {t('landing.hero.webchat.inputPlaceholder')}
              </span>
              <span
                aria-hidden="true"
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 9,
                  background: brand.ink,
                  color: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                  flex: 'none',
                }}
              >
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.2}
                  width={16}
                  height={16}
                >
                  <path d="m22 2-7 20-4-9-9-4 20-7z" />
                </svg>
              </span>
            </div>
            <div
              style={{
                textAlign: 'center',
                fontFamily: fonts.mono,
                fontSize: 10,
                color: brand.slate,
                marginTop: 7,
                letterSpacing: '0.04em',
              }}
            >
              {t('landing.hero.webchat.poweredBy')}{' '}
              <b style={{ color: brand.signalDeep, fontWeight: 600 }}>{t('productName')}</b>
            </div>
          </div>
        </div>

        {/* Cartão flutuante: encaminhamento com contexto */}
        <div
          data-decor=""
          style={{
            position: 'absolute',
            bottom: 26,
            left: 18,
            maxWidth: 236,
            display: 'flex',
            gap: 11,
            alignItems: 'flex-start',
            padding: '12px 13px',
            borderRadius: 14,
            background: brand.handoffSoft,
            border: `1px solid ${brand.handoff}55`,
            boxShadow: brand.shadowLg,
            animation: anim.approved,
          }}
        >
          <span
            aria-hidden="true"
            style={{
              width: 30,
              height: 30,
              borderRadius: 9,
              background: brand.handoff,
              color: '#fff',
              display: 'grid',
              placeItems: 'center',
              flex: 'none',
            }}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              width={16}
              height={16}
            >
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </span>
          <div>
            <b
              style={{
                fontFamily: fonts.display,
                fontSize: 13.5,
                color: brand.ink,
                display: 'block',
              }}
            >
              {t('landing.hero.webchat.handoffTitle')}
            </b>
            <span style={{ fontSize: 12.5, color: brand.inkSoft }}>
              {t('landing.hero.webchat.handoffDesc')}{' '}
              <span style={{ color: brand.handoffStrong, fontWeight: 600 }}>
                {t('landing.hero.webchat.handoffEtaLabel')} {t('landing.hero.webchat.handoffEta')}
              </span>
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
